import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { analytics } from '../services/analytics';
import SEOPageLayout from '../components/SEOPageLayout';

const TemplatesHub = () => {
  useEffect(() => {
    analytics.track('Templates Hub Page View');
  }, []);

  const templates = [
    {
      title: 'HVAC Tiered Proposal',
      description: 'Good/Better/Best HVAC system packages with efficiency ratings, warranties, and maintenance plans.',
      category: 'Home Services',
      link: '/templates/tiered-hvac-proposal-template',
      features: ['14-18 SEER options', 'Warranty tiers', 'Maintenance add-ons']
    },
    {
      title: 'Web Design Packages',
      description: 'Website packages from basic brochure sites to full e-commerce solutions with ongoing support.',
      category: 'Creative',
      link: '/templates/web-design-packages-template',
      features: ['Page count options', 'E-commerce setup', 'Support levels']
    },
    {
      title: 'Construction Bid Options',
      description: 'Construction and renovation quotes with material quality tiers and finishing options.',
      category: 'Home Services',
      link: '/templates/construction-bid-options-template',
      features: ['Material grades', 'Timeline options', 'Warranty coverage']
    },
    {
      title: 'Photography Packages',
      description: 'Wedding and event photography packages with coverage hours, albums, and delivery options.',
      category: 'Creative',
      link: '/templates/photography-pricing-packages',
      features: ['Coverage hours', 'Album options', 'Digital delivery']
    },
    {
      title: 'Landscaping Packages',
      description: 'Landscape maintenance and installation packages from basic care to premium designs.',
      category: 'Home Services',
      link: '/templates/landscaping-maintenance-packages',
      features: ['Service frequency', 'Plant quality', 'Design complexity']
    },
    {
      title: 'Auto Detailing Packages',
      description: 'Car detailing quotes from basic washes to full ceramic coatings with interior deep cleans.',
      category: 'Professional',
      link: '/templates/auto-detailing-packages-template',
      features: ['Wash tiers', 'Interior detail', 'Ceramic coating']
    },
    {
      title: 'Catering Packages',
      description: 'Event catering quotes from drop-off to full-service with staff and bar service.',
      category: 'Professional',
      link: '/templates/catering-packages-template',
      features: ['Drop-off vs buffet', 'Staffing levels', 'Menu options']
    },
    {
      title: 'Electrical Packages',
      description: 'Electrical service quotes from repairs to panel upgrades and full-home rewires.',
      category: 'Home Services',
      link: '/templates/electrical-packages-template',
      features: ['Repair vs upgrade', 'Warranty tiers', 'Panel capacity']
    },
    {
      title: 'Interior Design Packages',
      description: 'Interior design quotes from consultations to full-home turnkey design management.',
      category: 'Creative',
      link: '/templates/interior-design-packages-template',
      features: ['Consultation scope', '3D renders', 'Project management']
    },
    {
      title: 'Painting Packages',
      description: 'Painting quotes from single rooms to full interior makeovers with colour consultation.',
      category: 'Home Services',
      link: '/templates/painting-packages-template',
      features: ['Room count', 'Prep levels', 'Paint quality']
    },
    {
      title: 'Pest Control Packages',
      description: 'Pest control quotes from one-time treatments to quarterly and premium shield plans.',
      category: 'Home Services',
      link: '/templates/pest-control-packages-template',
      features: ['Treatment frequency', 'Coverage scope', 'Emergency callbacks']
    },
    {
      title: 'Plumbing Packages',
      description: 'Plumbing quotes from repairs to fixture installs and whole-home repiping.',
      category: 'Home Services',
      link: '/templates/plumbing-packages-template',
      features: ['Repair vs install', 'Fixture types', 'Warranty length']
    },
    {
      title: 'Pool Construction Packages',
      description: 'Pool construction quotes from basic vinyl pools to luxury resort-style builds.',
      category: 'Home Services',
      link: '/templates/pool-construction-packages-template',
      features: ['Pool type', 'Deck materials', 'Spa & features']
    },
    {
      title: 'Roofing Packages',
      description: 'Roofing quotes from patch repairs to partial and full roof replacements.',
      category: 'Home Services',
      link: '/templates/roofing-packages-template',
      features: ['Repair vs replace', 'Shingle grades', 'Warranty length']
    },
    {
      title: 'Solar Packages',
      description: 'Solar installation quotes from starter systems to premium battery-backed setups.',
      category: 'Home Services',
      link: '/templates/solar-packages-template',
      features: ['System size', 'Inverter type', 'Battery backup']
    },
    {
      title: 'Tree Service Packages',
      description: 'Tree care quotes from trimming to removal and full annual property maintenance.',
      category: 'Home Services',
      link: '/templates/tree-service-packages-template',
      features: ['Trim vs remove', 'Stump grinding', 'Annual plans']
    },
    {
      title: 'Videography Packages',
      description: 'Video production quotes from highlight reels to cinematic full-day coverage.',
      category: 'Creative',
      link: '/templates/videography-packages-template',
      features: ['Shoot duration', 'Edit length', 'Drone footage']
    }
  ];

  const categories = ['All', 'Home Services', 'Creative', 'Professional'];

  return (
    <SEOPageLayout
      title="Quote Templates | Free Interactive Pricing Templates | QuoteTier"
      description="Browse free quote templates for HVAC, web design, construction, photography, and more. Interactive Good/Better/Best pricing templates that increase deal size."
      canonical="/templates"
      ogTitle="Free Quote Templates - Good/Better/Best Pricing"
      ogDescription="Download interactive quote templates for any industry. HVAC proposals, web design packages, construction bids, photography pricing and more."
    >
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              Quote <span className="text-primary font-bold">templates</span> that convert
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
              Start with proven Good/Better/Best templates designed to increase your average deal size.
            </p>
            <Link
              to="/signup"
              onClick={() => analytics.track('CTA Clicked', { location: 'templates-hero', action: 'signup' })}
            >
              <Button className="px-8 py-4 font-instrument text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                Get Started Free
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-50 border-y border-gray-200">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                className={`px-4 py-2 rounded-full font-jetbrains text-sm transition-colors ${
                  category === 'All'
                    ? 'bg-black text-white'
                    : 'bg-white border border-gray-200 text-gray-700 hover:border-black'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Templates Grid */}
      <section className="py-16 md:py-24">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template, index) => (
              <Link
                key={index}
                to={template.link}
                className="group"
                onClick={() => analytics.track('Template Clicked', { template: template.title })}
              >
                <Card className="h-full border border-gray-200 hover:border-black hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-jetbrains text-xs text-gray-500 uppercase tracking-wider">
                        {template.category}
                      </span>
                    </div>
                    <CardTitle className="font-instrument text-xl">{template.title}</CardTitle>
                    <CardDescription className="font-jetbrains text-sm">
                      {template.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {template.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 font-jetbrains text-sm text-gray-600">
                          <span className="w-1.5 h-1.5 bg-black rounded-full"></span>
                          {feature}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button
                      variant="outline"
                      className="w-full border-gray-300 hover:border-black hover:bg-black hover:text-white transition-all font-jetbrains"
                    >
                      View Template
                      <span className="ml-2 inline-block transition-transform group-hover:translate-x-0.5">→</span>
                    </Button>
                  </CardFooter>
                </Card>
              </Link>
            ))}

            {/* Create Your Own Card */}
            <Card className="h-full border-2 border-dashed border-gray-300 hover:border-black transition-colors">
              <CardContent className="flex flex-col items-center justify-center h-full p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-3xl">+</span>
                </div>
                <h3 className="font-instrument text-xl font-bold mb-2">Create Your Own</h3>
                <p className="font-jetbrains text-sm text-gray-600 mb-6">
                  Build a custom quote template for your unique business needs
                </p>
                <Link
                  to="/signup"
                  onClick={() => analytics.track('CTA Clicked', { location: 'templates-create', action: 'signup' })}
                >
                  <Button className="bg-black text-white hover:bg-gray-800 font-jetbrains">
                    Start Building
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How Templates Work */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold text-center mb-12">
            How Our Templates Work
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                1
              </div>
              <h3 className="font-instrument text-xl font-bold mb-2">Choose a Template</h3>
              <p className="font-jetbrains text-gray-600">
                Pick a template designed for your industry with proven Good/Better/Best pricing tiers.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                2
              </div>
              <h3 className="font-instrument text-xl font-bold mb-2">Customize It</h3>
              <p className="font-jetbrains text-gray-600">
                Add your pricing, services, and branding. The structure is proven; you just fill in the details.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                3
              </div>
              <h3 className="font-instrument text-xl font-bold mb-2">Send & Win</h3>
              <p className="font-jetbrains text-gray-600">
                Send interactive quotes that help customers upsell themselves. Get signatures instantly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32">
        <div className="container max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Ready to use these templates?
          </h2>
          <p className="font-jetbrains text-xl text-gray-600 mb-10">
            Sign up free and start sending professional quotes that win more business.
          </p>
          <Link
            to="/signup"
            onClick={() => analytics.track('CTA Clicked', { location: 'templates-bottom', action: 'signup' })}
          >
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
              Get Started Free
            </Button>
          </Link>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default TemplatesHub;
